import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterAltOff as ClearFiltersIcon,
} from '@mui/icons-material';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { DataTable, useAppSnackbar } from '../../common/TableDesign';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';
import {
  RowActionsMenu,
  fmtDate,
  getErrorMessage,
  unwrap,
  unwrapList,
} from './CashShared';

const amt = (v) => formatAccountingAmount(v);

// Every GL account with an active reconciliation-accounts setup, its current
// balance and reconciliation status - a dashboard over cash/bank positions.
const BankAccountsTab = () => {
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ledgerRange, setLedgerRange] = useState({ start_date: '', end_date: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ledgerAPI.getBankingAccounts(search ? { search } : {});
      setItems(unwrapList(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch bank accounts'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = async (row, range = ledgerRange) => {
    setDetail({ row, data: null });
    setDetailLoading(true);
    try {
      const params = {};
      if (range.start_date) params.start_date = range.start_date;
      if (range.end_date) params.end_date = range.end_date;
      const res = await ledgerAPI.getBankingAccountDetail(row.account_id, params);
      setDetail({ row, data: unwrap(res) });
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch account detail'), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const applyLedgerRange = () => {
    if (detail?.row) openDetail(detail.row, ledgerRange);
  };

  const ledgerData = detail?.data?.ledger;
  const transactions = Array.isArray(ledgerData?.transactions) ? ledgerData.transactions : [];

  const exportCsv = () => {
    const headers = ['Account Code', 'Account Name', 'Institution', 'Type', 'External No.', 'Balance', 'Last Reconciled', 'Pending Matches'];
    const rows = items.map((a) => [
      a.account_code || '', a.account_name || '', a.institution_name || '',
      a.payment_method_type || '', a.external_account_no || '',
      Number(a.current_balance) || 0, a.last_reconciled_at || '', a.pending_matches ?? 0,
    ]);
    downloadCsv('bank-accounts.csv', headers, rows);
  };

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!items.length}>
              Export CSV
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Search"
              placeholder="Account, institution, external no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            {search && (
              <Button variant="outlined" size="small" startIcon={<ClearFiltersIcon />} onClick={() => setSearch('')}>
                Clear
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            {items.length === 0 && !loading ? (
              <EmptyRecordsState message="No bank accounts configured yet. Set one up under Reconciliation Accounts." />
            ) : (
              <DataTable
                columns={['Account', 'Type', 'External No.', 'Balance', 'Last Reconciled', 'Pending', 'Actions']}
                data={items}
                loading={loading}
                emptyMessage="No bank accounts found."
                minWidth={950}
                renderRow={(a) => (
                  <TableRow key={a.detail_id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(a)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{a.account_code} - {a.account_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.institution_name || '-'}</Typography>
                    </TableCell>
                    <TableCell>{a.payment_method_type || '-'}</TableCell>
                    <TableCell>{a.external_account_no || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{formatAccountingAmount(a.current_balance, { currency: a.currency_code || 'UGX' })}</strong>
                    </TableCell>
                    <TableCell>{fmtDate(a.last_reconciled_at)}</TableCell>
                    <TableCell>
                      {a.pending_matches > 0 ? (
                        <Chip size="small" color="warning" label={a.pending_matches} />
                      ) : (
                        <Chip size="small" color="success" variant="outlined" label="0" />
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        items={[{ icon: <ViewIcon fontSize="small" />, label: 'View account', onClick: () => openDetail(a) }]}
                      />
                    </TableCell>
                  </TableRow>
                )}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{detail?.row?.account_code} - {detail?.row?.account_name}</DialogTitle>
        <DialogContent>
          {detailLoading || !detail?.data ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : (
            <>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Type</Typography>
                  <Typography variant="body2">{detail.data.configuration?.payment_method_type || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">External No.</Typography>
                  <Typography variant="body2">{detail.data.configuration?.external_account_no || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Institution</Typography>
                  <Typography variant="body2">{detail.data.configuration?.institution_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Account Holder</Typography>
                  <Typography variant="body2">{detail.data.configuration?.account_holder_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Branch / SWIFT / Routing</Typography>
                  <Typography variant="body2">
                    {[detail.data.configuration?.branch_code, detail.data.configuration?.swift_code, detail.data.configuration?.routing_number].filter(Boolean).join(' / ') || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Auto-reconcile</Typography>
                  <Typography variant="body2">{detail.data.configuration?.auto_reconcile ? `Yes (day ${detail.data.configuration?.reconciliation_day ?? '-'})` : 'No'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Current Balance</Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatAccountingAmount(detail.data.account?.current_balance, { currency: detail.data.account?.currency_code || 'UGX' })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Balance Period</Typography>
                  <Typography variant="body2">{detail.data.account?.balance_period_name || '-'}</Typography>
                </Grid>
              </Grid>

              {detail.data.latest_reconciliation && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Latest reconciliation</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={3}>
                    <Typography variant="body2">Statement: {amt(detail.data.latest_reconciliation.statement_balance)}</Typography>
                    <Typography variant="body2">Book: {amt(detail.data.latest_reconciliation.book_balance)}</Typography>
                    <Typography variant="body2" color={Math.abs(detail.data.latest_reconciliation.difference) < 0.005 ? 'success.main' : 'error.main'} fontWeight={700}>
                      Difference: {amt(detail.data.latest_reconciliation.difference)}
                    </Typography>
                    <Chip size="small" label={detail.data.latest_reconciliation.is_reconciled ? 'Reconciled' : 'Pending'} color={detail.data.latest_reconciliation.is_reconciled ? 'success' : 'warning'} />
                  </Stack>
                </Box>
              )}

              {Array.isArray(detail.data.reconciliation_history) && detail.data.reconciliation_history.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Reconciliation history</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Statement Date</TableCell>
                        <TableCell align="right">Statement Balance</TableCell>
                        <TableCell align="right">Difference</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.data.reconciliation_history.map((r) => (
                        <TableRow key={r.reconciliation_id}>
                          <TableCell>{fmtDate(r.statement_date)}</TableCell>
                          <TableCell align="right">{amt(r.statement_balance)}</TableCell>
                          <TableCell align="right">{amt(r.difference)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={r.is_reconciled ? 'Reconciled' : `${r.pending_matches} pending`} color={r.is_reconciled ? 'success' : 'warning'} variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">Ledger transactions</Typography>
                <TextField size="small" type="date" label="From" value={ledgerRange.start_date} onChange={(e) => setLedgerRange((p) => ({ ...p, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={ledgerRange.end_date} onChange={(e) => setLedgerRange((p) => ({ ...p, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
                <Button size="small" variant="outlined" onClick={applyLedgerRange}>Apply</Button>
              </Box>
              {transactions.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No transactions in range.</Typography>
              ) : (
                <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Entry</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Debit</TableCell>
                        <TableCell align="right">Credit</TableCell>
                        <TableCell align="right">Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((t, i) => (
                        <TableRow key={t.entry_line_id ?? t.line_id ?? i}>
                          <TableCell>{fmtDate(t.entry_date ?? t.date)}</TableCell>
                          <TableCell>{t.entry_number ?? t.reference ?? '-'}</TableCell>
                          <TableCell>{t.description ?? '-'}</TableCell>
                          <TableCell align="right">{amt(t.debit_amount)}</TableCell>
                          <TableCell align="right">{amt(t.credit_amount)}</TableCell>
                          <TableCell align="right">{amt(t.running_balance ?? t.balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {SnackbarComponent}
    </>
  );
};

export default BankAccountsTab;
